import { request } from '@/lib/apiClient'

// smart search now routes entirely through the backend: one authenticated
// POST /api/smart-search (via apiClient → ${API_BASE_URL}, backend JWT) that
// BOTH generates the SQL and executes it server-side, returning rows. no
// relative fetch, no supabase.auth, no direct supabase.rpc — those were the
// four pre-migration breaks. request() already reads status/body safely, so a
// non-JSON / error response surfaces as a real message, not a JSON.parse error.

export interface SmartSearchResult {
  query: string
  sql: string
  results: any[]
  columns: string[]
  tables_used: string[]   // primary table first — drives row click-through
  explanation: string
  executionTime: number
  // whether a row can be opened at all: the server rewrites the projection
  // to carry the primary key, but a query over a table we cannot route to
  // still has nowhere to go. Say so rather than rendering dead rows.
  openable?: boolean
  notOpenableReason?: string | null
  error?: {
    message: string
    stage?: string   // rate_limit | input | generate | validate | execute
    sql?: string     // present on validate/execute failures, for the optional SQL toggle
  }
}

export interface SearchSuggestion {
  text: string
  description: string
  example: string
}

interface SmartSearchApiResponse {
  sql: string
  explanation: string
  tables_used: string[]
  results: any[]
  columns: string[]
  row_count: number
  execution_ms: number
  openable?: boolean
  not_openable_reason?: string | null
  id_rewritten?: boolean
  primary_table?: string | null
  enriched_rows?: number
  // present only on error responses
  error?: string
  stage?: string
}

export class SmartSearchService {
  async searchNaturalLanguage(query: string): Promise<SmartSearchResult> {
    const startTime = Date.now()
    const { data, error } = await request<SmartSearchApiResponse>('POST', '/api/smart-search', { query })

    // apiClient surfaces the backend's real error message (and never throws a
    // JSON.parse error on a non-JSON body). the validate/execute stages also
    // return the sql + explanation alongside the error for the SQL toggle —
    // carried on error.body, since data is always null on a non-2xx.
    if (error || !data) {
      const body = (error?.body ?? {}) as Partial<SmartSearchApiResponse>
      return {
        query, sql: body.sql ?? '', results: [], columns: [], tables_used: [],
        explanation: body.explanation ?? '',
        executionTime: Date.now() - startTime,
        error: { message: error?.message ?? 'Search failed', stage: body.stage, sql: body.sql },
      }
    }

    return {
      query,
      sql: data.sql,
      results: data.results ?? [],
      columns: data.columns ?? [],
      tables_used: data.tables_used ?? [],
      explanation: data.explanation,
      executionTime: data.execution_ms ?? (Date.now() - startTime),
      openable: data.openable ?? true,
      notOpenableReason: data.not_openable_reason ?? null,
    }
  }

  // resolve a result row (table + id) to the app route where it lives.
  // null = not resolvable (deleted row, unmapped assessment, …)
  async locate(table: string, id: string | number): Promise<string | null> {
    const { data } = await request<{ route: string }>(
      'GET', `/api/smart-search/locate?table=${encodeURIComponent(table)}&id=${encodeURIComponent(String(id))}`)
    return data?.route ?? null
  }

  getSuggestions(): SearchSuggestion[] {
    return [
      { text: 'Systems by vendor',      description: 'Find all systems supplied by a specific vendor', example: 'Show me all systems from Kongsberg' },
      { text: 'High-risk systems',      description: 'Risk-assessment rows with a high risk level',     example: 'Which systems have the highest risk level?' },
      { text: 'Systems by vessel',      description: 'Systems belonging to a specific hull or vessel',   example: 'List systems on hull SN2729' },
      { text: 'Type-approved systems',  description: 'Systems that carry a type approval',                example: 'Show systems with type approval' },
      { text: 'Asset inventory search', description: 'Search equipment in the vessel asset inventory',    example: 'Find equipment made by Siemens in the engine room' },
      { text: 'CAT I systems',          description: 'Category I (critical) assessed systems',            example: 'Show me CAT I systems' },
      { text: 'Vendor contacts',        description: 'Contact details for a company',                     example: 'Find contacts at Wartsila' },
      { text: 'Excluded assets',        description: 'Asset-inventory items marked out of scope',         example: 'List excluded equipment' },
    ]
  }
}

export const smartSearchService = new SmartSearchService()
